import { Stack, Typography } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import { useHistory } from 'react-router';

import SuppressedIcon from '@sbu/assets.svg/src/suppressed.svg';
import { Checkbox } from '@sbu/components.checkbox';
import { getSignalSeverityStatusColor } from '@sbu/utils.signals';

import { EntityListFragment as EntityType } from 'core/graphql';
import { SortDirection } from 'shared/components/data/Sort';
import { EntityLink } from 'shared/components/internal/EntityLink';
import { ValueBasedFilterTypes } from 'shared/components/internal/Filter';
import { SortProps } from 'shared/components/internal/SortMenu/sortOptions/entity';
import { onTagClick, TagChips } from 'shared/components/internal/Tags';
import { ListItem } from 'shared/components/layout/List';
import { Column } from 'shared/components/layout/Row';
import { SelectionItem } from 'shared/components/utils/Selection';
import { Slant } from 'shared/components/utils/Slant';

import css from './EntityRow.module.scss';
import { EntityRowScoreContainer } from './EntityRowScore';

interface EntityReferrerType {
  offset: number;
  queryString: string;
  sorts: SortDirection<SortProps>[];
}

interface EntityRowProps {
  entity: EntityType;
  referrerProps?: EntityReferrerType;
  threshold?: number | null;
  onQuickFilter?: (filter: ValueBasedFilterTypes) => void;
}

export const EntityRow = ({
  entity,
  threshold,
  referrerProps,
}: EntityRowProps) => {
  const history = useHistory();

  const {
    activityScore,
    id,
    isWhitelisted,
    entityTypeName,
    recentSignalSeverity,
    suppressListsObj,
    tags,
    criticality,
  } = entity;

  const signalSeverityStatusColor = getSignalSeverityStatusColor(
    recentSignalSeverity ?? 0,
    threshold
  );

  return (
    <ListItem className={css.entityListItem}>
      <Slant>
        <div className={css.row} data-sumo-test={`entity-row-${id}`}>
          <Column>
            <div className={css.checkboxCell}>
              <SelectionItem model={id}>
                <Checkbox
                  CheckboxProps={{ sx: { padding: 0 } }}
                  hideLabel
                  label='Select entity'
                  sx={{ mr: 3 }}
                />
              </SelectionItem>
            </div>
          </Column>

          {/* Entity Name and Label */}
          <Stack
            className={css.column}
            component={Column}
            display='block'
            flex={1}
            gap={1}
            margin={0}
          >
            <Typography variant='subtitle1'>{entityTypeName}</Typography>
            <EntityLink
              entityId={entity.id}
              entityValue={entity.value}
              sensorZone={entity.sensorZone}
              reputationData={{ ...entity }}
              referrerProps={referrerProps}
              isSuppressed={entity.isWhitelisted}
              showSuppression={true}
            />
            {!!tags.length && (
              <div className={css.entityRowTags}>
                <TagChips
                  onClick={(tag) => onTagClick(history, tag)}
                  tags={tags}
                />
              </div>
            )}
          </Stack>

          {/* Whitelisted/Suppressed */}
          {isWhitelisted && (
            <Column className={classNames(css.column, css.suppressed)}>
              <SuppressedIcon className={css.suppressedIcon} /> Suppressed
            </Column>
          )}

          {/* Suppressed Lists */}
          <Stack
            className={css.column}
            component={Column}
            display='block'
            gap={1}
            margin={0}
          >
            <Typography variant='subtitle1'>Suppressed Lists</Typography>
            {suppressListsObj.length ? (
              <Stack gap={0.5}>
                {suppressListsObj.map((suppressedListObj) => {
                  return (
                    <Typography
                      key={suppressedListObj.id}
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {suppressedListObj.name}
                    </Typography>
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ wordBreak: 'break-word' }}>None</Typography>
            )}
          </Stack>

          {/* Criticality */}
          <Stack
            className={css.column}
            component={Column}
            display='block'
            gap={1}
            margin={0}
          >
            <Typography variant='subtitle1'>Criticality</Typography>
            <Typography sx={{ wordBreak: 'break-word' }}>
              {criticality ?? 'Default'}
            </Typography>
          </Stack>

          {/* Activity Score */}
          <Stack
            className={css.column}
            component={Column}
            display='block'
            gap={1}
            margin={0}
          >
            <Typography variant='subtitle1'>Activity Score</Typography>
            <EntityRowScoreContainer current={activityScore} />
          </Stack>

          {/* Signal Severity Total */}
          <Stack
            className={css.column}
            component={Column}
            display='block'
            gap={1}
            margin={0}
          >
            <Typography variant='subtitle1'>Signal Severity Total</Typography>
            <Typography
              color={signalSeverityStatusColor}
              sx={{ wordBreak: 'break-word' }}
            >
              {recentSignalSeverity}
            </Typography>
          </Stack>
        </div>
      </Slant>
    </ListItem>
  );
};
